import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type DBUser, type Database, type Client, type Node } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, ErrorBox, Badge } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

export function DBUsersPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [grantUser, setGrantUser] = useState<DBUser | null>(null)
  const [pwUser, setPwUser] = useState<DBUser | null>(null)
  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: users = [] } = useQuery({ queryKey: ['db-users'], queryFn: async () => (await api.get<DBUser[]>('/db-users')).data })
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => (await api.get<Client[]>('/clients')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]))
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  const [fNode, setFNode] = useState('')
  const [fClient, setFClient] = useState(0)
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = users
    if (fClient) list = list.filter(u => u.client_id === fClient)
    if (fNode) {
      const ids = new Set(clients.filter(c => c.node_id === fNode).map(c => c.id))
      list = list.filter(u => ids.has(u.client_id))
    }
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(u => u.name.toLowerCase().includes(q))
    }
    return list
  }, [users, fNode, fClient, fSearch, clients])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/db-users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['db-users'] }),
  })

  return (
    <>
      <PageHeader
        title="Пользователи БД"
        subtitle="Учётные записи MySQL. Права выдаются на конкретные базы через «Права»."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить пользователя</Button>}
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Логин…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
        <FilterSelect value={fClient} onChange={setFClient} options={[
          { value: 0, label: 'Все клиенты' },
          ...clients.map(c => ({ value: c.id, label: c.unix_user })),
        ]} />
      </FilterBar>
      <Table<DBUser>
        rows={filtered} empty={users.length === 0 ? 'Нет пользователей БД.' : 'Ничего не найдено.'}
        cols={[
          { key: 'n', label: 'Логин', render: (u) => <span className="font-mono font-medium">{u.name}</span> },
          { key: 'h', label: 'Хост', render: (u) => <Badge tone="neutral">{u.host}</Badge> },
          { key: 'nd', label: 'Нода', render: (u) => {
            const c = clientMap[u.client_id]
            return <span className="text-[13px] text-neutral-700">{c ? (nodeMap[c.node_id]?.name || '—') : '—'}</span>
          }},
          { key: 'c', label: 'Клиент', render: (u) => <span className="font-mono text-[12px]">{clientMap[u.client_id]?.unix_user || u.client_id}</span> },
          { key: 'act', label: '', w: '1%', render: (u) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Права', onClick: () => setGrantUser(u) },
                { label: 'Сменить пароль', onClick: () => setPwUser(u) },
                { divider: true, label: '', onClick: () => {} },
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить ${u.name}?`) && del.mutate(u.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddDBUserModal clients={clients} onClose={() => setAddOpen(false)}
        onTask={(t) => { setAddOpen(false); setTaskId(t); qc.invalidateQueries({ queryKey: ['db-users'] }) }} />}
      {grantUser && <GrantModal user={grantUser} onClose={() => setGrantUser(null)}
        onTask={(t) => { setGrantUser(null); setTaskId(t) }} />}
      {pwUser && <PasswordModal user={pwUser} onClose={() => setPwUser(null)}
        onTask={(t) => { setPwUser(null); setTaskId(t) }} />}
      {taskId && <TaskProgress taskId={taskId} onClose={() => setTaskId(null)} />}
    </>
  )
}

function AddDBUserModal({ clients, onClose, onTask }: { clients: Client[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ client_id: clients[0]?.id || 0, name: '', host: 'localhost', password: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/db-users', f)).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Создать пользователя БД">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Клиент">
          <Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: +e.target.value })}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.unix_user}</option>)}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Логин"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="myuser" required autoFocus /></Field>
          <Field label="Хост"><Input value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} /></Field>
        </div>
        <Field label="Пароль"><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Создаём…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}

function GrantModal({ user, onClose, onTask }: { user: DBUser; onClose: () => void; onTask: (id: string) => void }) {
  const { data: dbs = [] } = useQuery({ queryKey: ['databases'], queryFn: async () => (await api.get<Database[]>('/databases')).data })
  const clientDbs = dbs.filter(d => d.client_id === user.client_id)
  const [dbId, setDbId] = useState(clientDbs[0]?.id || 0)
  const [err, setErr] = useState('')
  const grant = useMutation({
    mutationFn: async () => (await api.post(`/db-users/${user.id}/grants`, { database_id: dbId, privileges: ['ALL'] })).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title={`Права: ${user.name}`}>
      <form onSubmit={(e) => { e.preventDefault(); grant.mutate() }} className="space-y-4">
        <p className="text-[13px] text-neutral-600">Даёт пользователю все права (ALL) на выбранную базу этого же клиента.</p>
        <Field label="База данных">
          <Select value={dbId} onChange={(e) => setDbId(+e.target.value)}>
            {clientDbs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={grant.isPending || !dbId}>Выдать права</Button>
        </div>
      </form>
    </Modal>
  )
}

function PasswordModal({ user, onClose, onTask }: { user: DBUser; onClose: () => void; onTask: (id: string) => void }) {
  const [pw, setPw] = useState('')
  const [err, setErr] = useState('')
  const submit = useMutation({
    mutationFn: async () => (await api.post(`/db-users/${user.id}/password`, { password: pw })).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title={`Смена пароля: ${user.name}`}>
      <form onSubmit={(e) => { e.preventDefault(); submit.mutate() }} className="space-y-4">
        <Field label="Новый пароль"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoFocus /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={submit.isPending}>Сохранить</Button>
        </div>
      </form>
    </Modal>
  )
}
